import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DailyStatusGate.css';
import { measureStatusReveal } from './dailyStatusGateMotion';
import { lockDailyStatusDocument } from './dailyStatusDocumentLock';
import { isForeground, subscribeForeground } from '../services/AppStateService';
import { statusCheckInErrorMessage } from './statusCheckInError';
import { beginCoachFlow, bindCoachFlowSubject, failCoachFlow, markCoachFlow } from '../services/coachSequenceTiming';

const OPTIONS = [
  ['great', '🔥', 'Feeling great'], ['good', '😊', 'Feeling good'],
  ['okay', '😐', 'Feeling okay'], ['tired', '😴', 'Feeling tired'],
  ['stressed', '😤', 'Feeling stressed'], ['recovering', '🩹', 'Recovering'],
  ['sick', '🤒', 'Feeling sick'], ['injured', '🤕', 'Injured'],
  ['complicated', '🧭', 'Something else'],
];
// Positive check-ins save from one tap; everything else asks for context so
// the Coach knows what to adapt before the day starts.
const NOTE_REQUIRED = new Set(['tired', 'stressed', 'recovering', 'sick', 'injured', 'complicated']);
const NOTE_MAX_LENGTH = 500;
const STATUS_RESOLUTION_TIMEOUT_MS = 8000;
const STATUS_RECHECK_INTERVAL_MS = 60000;

export default function DailyStatusGate({ backendService, children, onReady }) {
  const [resolution, setResolution] = useState('resolving');
  const [step, setStep] = useState('choices');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [motion, setMotion] = useState({ x: 0, y: 0, scaleX: 1, scaleY: 1 });
  const shellRef = useRef(null);
  const headingRef = useRef(null);
  const choiceRefs = useRef({});
  const pickedRef = useRef(null);
  const noteRef = useRef(null);
  const returnFocusRef = useRef(null);
  // Set synchronously so a double tap cannot start a second write before the
  // disabled state renders. Stays set after success until the gate reopens.
  const savingRef = useRef(false);
  const unlockTimerRef = useRef(null);
  const readyAnnouncedRef = useRef(false);
  const statusCheckRequestRef = useRef(0);
  const lastAppliedIsTodayRef = useRef(null);
  const confirmedAtRef = useRef(null);
  // Match the server's member-local calendar, not the device's timezone. Until
  // the profile is available, do not mistake an assumed UTC day for authority.
  const isSameMemberDay = (timestamp, now = Date.now()) => {
    if (!backendService.user || timestamp === null) return null;
    try {
      const format = new Intl.DateTimeFormat('en-CA', {
        timeZone: backendService.user.timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
      });
      return format.format(timestamp) === format.format(now);
    } catch (_) {
      return null;
    }
  };
  const sameMemberDayRef = useRef(isSameMemberDay);
  sameMemberDayRef.current = isSameMemberDay;
  const shouldLockDocumentScroll = resolution !== 'unlocked';
  const revealingFeed = resolution === 'revealing';

  useLayoutEffect(() => {
    if (!shouldLockDocumentScroll) return undefined;
    // Release the previous lock before resetting, then capture zero in the new
    // lock. Otherwise its cleanup would restore yesterday's reading position.
    // Layout timing puts the feed at the top before the reveal can paint.
    if (revealingFeed) window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    return lockDailyStatusDocument();
  }, [shouldLockDocumentScroll, revealingFeed]);

  useEffect(() => {
    let active = true;
    let initialSettled = false;
    let initialRequestId = null;
    const settleInitial = () => {
      if (!active || initialSettled) return;
      initialSettled = true;
      window.clearTimeout(timeout);
    };
    const applyStatus = (latest, requestId, checkedAt = Date.now()) => {
      if (!active) return;
      // Even a stale successful response proves the startup request did not
      // time out. It may disarm the fallback, but only the latest request may
      // change the rendered readiness state.
      settleInitial();
      if (requestId !== statusCheckRequestRef.current) return;
      if (unlockTimerRef.current) {
        window.clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = null;
      }
      // The DTO's canonical creation instant can prove a newly answered day
      // even if this GET began before midnight. Otherwise conservatively date
      // the server's is_today observation at request dispatch, not delivery.
      const createdAt = Date.parse(latest?.created_at);
      const answeredAt = Number.isFinite(createdAt) ? createdAt : checkedAt;
      if (latest?.is_today === true && sameMemberDayRef.current(answeredAt) !== false) {
        confirmedAtRef.current = answeredAt;
        lastAppliedIsTodayRef.current = true;
        setResolution('unlocked');
        return;
      }
      if (lastAppliedIsTodayRef.current !== false) {
        savingRef.current = false;
        returnFocusRef.current = null;
        setStep('choices');
        setSelected(null);
        setNote('');
        setError('');
      }
      lastAppliedIsTodayRef.current = false;
      setResolution('gated');
    };
    const timeout = window.setTimeout(
      () => {
        if (!active || initialSettled) return;
        initialSettled = true;
        // The fallback is a deliberate fail-closed resolution. Ignore the
        // startup read if it arrives later; a fresh foreground/interval check
        // can still unlock from newer server state.
        if (statusCheckRequestRef.current === initialRequestId) {
          statusCheckRequestRef.current += 1;
        }
        lastAppliedIsTodayRef.current = false;
        setResolution('gated');
      },
      STATUS_RESOLUTION_TIMEOUT_MS
    );
    let lastCheckAt = null;
    let recheckTimeout = null;
    const checkStatus = (initial = false) => {
      const checkedAt = Date.now();
      const requestId = ++statusCheckRequestRef.current;
      lastCheckAt = checkedAt;
      // Expire a previously verified day before waiting for the network. A
      // suspended WebView can return while its GET is offline or never settles.
      if (!initial && lastAppliedIsTodayRef.current === true &&
          sameMemberDayRef.current(confirmedAtRef.current) === false) {
        applyStatus(null, requestId);
      }
      window.clearTimeout(recheckTimeout);
      if (!initial && initialSettled) {
        recheckTimeout = window.setTimeout(() => {
          if (active && requestId === statusCheckRequestRef.current &&
              sameMemberDayRef.current(confirmedAtRef.current) !== true) {
            applyStatus(null, requestId);
            statusCheckRequestRef.current += 1;
          }
        }, STATUS_RESOLUTION_TIMEOUT_MS);
      }
      backendService.getLatestStatus()
        .then(latest => {
          if (requestId === statusCheckRequestRef.current) window.clearTimeout(recheckTimeout);
          applyStatus(latest, requestId, checkedAt);
        })
        .catch(() => {
          if (requestId === statusCheckRequestRef.current) window.clearTimeout(recheckTimeout);
          if (requestId === statusCheckRequestRef.current &&
              (initial || (initialSettled && sameMemberDayRef.current(confirmedAtRef.current) !== true))) {
            window.clearTimeout(recheckTimeout);
            applyStatus(null, requestId);
          }
        });
      return requestId;
    };
    initialRequestId = checkStatus(true);
    let intervalId = null;
    const stopPolling = () => {
      if (intervalId !== null) window.clearInterval(intervalId);
      intervalId = null;
    };
    const startPolling = () => {
      stopPolling();
      intervalId = window.setInterval(() => {
        if (isForeground()) checkStatus(false);
      }, STATUS_RECHECK_INTERVAL_MS);
    };
    const unsubscribeForeground = subscribeForeground(foreground => {
      if (!foreground) {
        stopPolling();
        return;
      }
      checkStatus(false);
      startPolling();
    });
    if (isForeground()) startPolling();
    // Some WebViews resume without first reporting hidden. Revalidate readiness
    // locally; never synthesize a global foreground event (GPS must stay paused
    // when native AppState still reports inactive).
    const wake = () => {
      if (!isForeground()) return;
      const expired = lastAppliedIsTodayRef.current === true &&
        sameMemberDayRef.current(confirmedAtRef.current) === false;
      if (!expired && lastCheckAt !== null && Date.now() - lastCheckAt < 1000) return;
      checkStatus(false);
      startPolling();
    };
    window.addEventListener('focus', wake);
    window.addEventListener('pageshow', wake);
    return () => {
      active = false;
      statusCheckRequestRef.current += 1;
      window.clearTimeout(timeout);
      window.clearTimeout(recheckTimeout);
      window.removeEventListener('focus', wake);
      window.removeEventListener('pageshow', wake);
      stopPolling();
      unsubscribeForeground();
    };
  }, [backendService]);

  useEffect(() => {
    if (resolution === 'resolving' || readyAnnouncedRef.current) return;
    readyAnnouncedRef.current = true;
    onReady?.();
  }, [resolution, onReady]);

  useLayoutEffect(() => {
    const app = shellRef.current?.closest('.App');
    if (!app) return undefined;
    const locked = resolution === 'resolving' || resolution === 'gated';
    app.classList.toggle('daily-status-app-locked', locked);
    if (locked) {
      app.setAttribute('aria-hidden', 'true');
      app.setAttribute('inert', '');
    } else {
      app.removeAttribute('aria-hidden');
      app.removeAttribute('inert');
    }
    return () => {
      app.classList.remove('daily-status-app-locked');
      app.removeAttribute('aria-hidden');
      app.removeAttribute('inert');
    };
  }, [resolution]);

  useEffect(() => () => {
    if (unlockTimerRef.current) window.clearTimeout(unlockTimerRef.current);
  }, []);

  useEffect(() => {
    if (resolution === 'gated') headingRef.current?.focus();
  }, [resolution]);

  useEffect(() => {
    if (step === 'details') {
      noteRef.current?.focus();
    } else if (returnFocusRef.current) {
      choiceRefs.current[returnFocusRef.current]?.focus();
      returnFocusRef.current = null;
    }
  }, [step]);

  const busy = saving || resolution !== 'gated';

  const save = async (status, privateNote = null) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const coachFlow = beginCoachFlow('readiness_review', typeof window !== 'undefined' ? window.event?.timeStamp : undefined);
    const landingElement = step === 'details' ? pickedRef.current : choiceRefs.current[status];
    let saved = false;
    setSaving(true);
    setError('');
    try {
      markCoachFlow(coachFlow, 'save_start');
      const savedStatus = await backendService.setUserStatus(status, privateNote, 'morning').catch(error => { failCoachFlow(coachFlow); throw error; });
      markCoachFlow(coachFlow, 'save_end');
      bindCoachFlowSubject(coachFlow, String(savedStatus?._id || ''));
      // The write is newer than any status GET already in flight. Invalidate
      // those reads so a pre-save `is_today: false` response cannot re-lock the
      // app after this successful answer has revealed today's feed.
      statusCheckRequestRef.current += 1;
      lastAppliedIsTodayRef.current = true;
      const savedAt = Date.parse(savedStatus?.created_at);
      confirmedAtRef.current = Number.isFinite(savedAt) ? savedAt : Date.now();
      saved = true;
      setMotion(measureStatusReveal(
        landingElement?.getBoundingClientRect(),
        document.querySelector('.status-dropdown-trigger')?.getBoundingClientRect()
      ));
      window.dispatchEvent(new CustomEvent('daily-status-saved', {
        detail: {
          status,
          statusContextId: savedStatus?._id || null,
          statusCreatedAt: savedStatus?.created_at || null,
          externalDailyTaskId: savedStatus?.external_daily_task_id || null,
          externalDailyFailureCode: savedStatus?.external_daily_failure_code || null,
          externalDailyCapturePending: savedStatus?.external_daily_capture_pending === true,
        },
      }));
      setSelected(status);
      setResolution('revealing');
      if (unlockTimerRef.current) window.clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = window.setTimeout(
        () => {
          unlockTimerRef.current = null;
          setResolution('unlocked');
        },
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 0 : 650
      );
    } catch (error) {
      setError(statusCheckInErrorMessage(error, 'gate'));

    } finally {
      if (!saved) savingRef.current = false;
      setSaving(false);
    }
  };

  const choose = status => {
    if (busy || savingRef.current) return;
    if (status !== selected) setNote('');
    setSelected(status);
    setError('');
    if (NOTE_REQUIRED.has(status)) setStep('details');
    else save(status);
  };

  const backToChoices = () => {
    if (busy || savingRef.current) return;
    returnFocusRef.current = selected;
    setError('');
    setStep('choices');
  };

  const submitNote = event => {
    event.preventDefault();
    const trimmed = note.trim();
    if (busy || !trimmed || !selected) return;
    save(selected, trimmed.slice(0, NOTE_MAX_LENGTH));
  };

  const handleDetailsKeyDown = event => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    backToChoices();
  };

  const pickedOption = OPTIONS.find(option => option[0] === selected);
  const visibleShell = resolution === 'unlocked' || resolution === 'revealing';
  const gate = (resolution === 'gated' || resolution === 'revealing') ? (
    <main
      data-status-gate
      className={`daily-status-gate daily-status-gate--${resolution}`}
      style={{
        '--status-reveal-x': `${motion.x}px`,
        '--status-reveal-y': `${motion.y}px`,
        '--status-reveal-scale-x': motion.scaleX,
        '--status-reveal-scale-y': motion.scaleY,
      }}
    >
      <div className="daily-status-gate-card">
        <p className="daily-status-gate-kicker">Daily readiness</p>
        {step === 'choices' ? (
          <>
            <h1 id="daily-status-heading" ref={headingRef} tabIndex={-1}>How do you feel?</h1>
            <p className="daily-status-gate-copy">A quick check-in helps your Coach shape today around you.</p>
            <ul className="status-gate-choices" aria-labelledby="daily-status-heading">
              {OPTIONS.map(([value, emoji, label]) => (
                <li key={value}>
                  <button
                    ref={element => { choiceRefs.current[value] = element; }}
                    type="button"
                    className={`status-gate-choice${selected === value ? ' is-selected' : ''}`}
                    aria-disabled={busy ? 'true' : undefined}
                    onClick={() => choose(value)}
                  >
                    <span className="status-gate-choice-emoji" aria-hidden="true">{emoji}</span>
                    <span className="status-gate-choice-label">{label}</span>
                    {NOTE_REQUIRED.has(value) && <span className="status-gate-choice-next" aria-hidden="true">›</span>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <form className="status-gate-details" onSubmit={submitNote} onKeyDown={handleDetailsKeyDown} noValidate>
            <p className="status-gate-picked" ref={pickedRef}>
              <span aria-hidden="true">{pickedOption?.[1]}</span>
              <span>{pickedOption?.[2]}</span>
            </p>
            <h1 id="daily-status-heading">Tell your Coach a bit more</h1>
            <div className="status-gate-note">
              <label htmlFor="daily-status-note">
                {selected === 'complicated' ? 'What’s going on today?' : 'What should your Coach know about today?'}
              </label>
              <textarea
                id="daily-status-note"
                ref={noteRef}
                required
                aria-describedby="daily-status-note-hint"
                maxLength={NOTE_MAX_LENGTH}
                readOnly={busy}
                value={note}
                onChange={event => setNote(event.target.value)}
              />
              <p className="status-gate-note-meta">
                <span id="daily-status-note-hint">Required · up to {NOTE_MAX_LENGTH} characters</span>
                <span aria-hidden="true">{note.length}/{NOTE_MAX_LENGTH}</span>
              </p>
            </div>
            <div className="status-gate-actions">
              <button
                type="button"
                className="status-gate-back"
                aria-disabled={busy ? 'true' : undefined}
                onClick={backToChoices}
              >
                Back
              </button>
              <button
                type="submit"
                className="status-gate-submit-note"
                disabled={!note.trim()}
                aria-disabled={busy ? 'true' : undefined}
              >
                {saving ? 'Saving…' : 'Save check-in'}
              </button>
            </div>
          </form>
        )}
        <p className="status-gate-saving" role="status">{saving ? 'Saving your check-in…' : ''}</p>
        {error && <p className="status-gate-error" role="alert">{error}</p>}

      </div>
    </main>
  ) : null;

  return (
    <>
      <div
        ref={shellRef}
        data-gated-shell
        className={`daily-gated-shell ${resolution === 'revealing' ? 'daily-gated-shell--revealing' : ''}`}
        aria-hidden={!visibleShell ? 'true' : undefined}
        inert={!visibleShell ? '' : undefined}
        hidden={resolution === 'resolving'}
      >
        {children}
      </div>
      {gate && createPortal(gate, document.body)}
    </>
  );
}
